import { useState, useCallback } from 'react';
import { Plus, ToggleLeft, ToggleRight, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useApi } from '../hooks/useApi.js';
import { post, put, del } from '../lib/api.js';

interface ConditionalAccessPolicy {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
  conditions: {
    trust_level?: string[];
    trust_score_below?: number;
    os_types?: string[];
    device_groups?: string[];
    user_roles?: string[];
    user_groups?: string[];
    user_emails?: string[];
    ip_ranges?: string[];
    countries?: string[];
    outside_known_locations?: boolean;
    outside_business_hours?: boolean;
    days_of_week?: number[];
  };
  actions: {
    effect: 'allow' | 'block' | 'limit' | 'step_up_auth';
    blocked_products?: string[];
    allowed_products?: string[];
    require_mfa?: boolean;
    mfa_frequency_hours?: number;
    session_max_hours?: number;
    read_only?: boolean;
    message?: string;
  };
  created_at: string;
  updated_at: string;
}

const EFFECT_BADGES: Record<string, { bg: string; text: string; label: string }> = {
  allow: { bg: 'bg-green-100', text: 'text-green-700', label: 'Allow' },
  block: { bg: 'bg-red-100', text: 'text-red-700', label: 'Block' },
  limit: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Limit' },
  step_up_auth: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Step-Up Auth' },
};

type PolicyForm = {
  id?: string;
  name: string;
  description: string;
  enabled: boolean;
  conditions: ConditionalAccessPolicy['conditions'];
  actions: ConditionalAccessPolicy['actions'];
};

const EMPTY_POLICY: PolicyForm = {
  name: '',
  description: '',
  enabled: true,
  conditions: {},
  actions: { effect: 'block' },
};

function conditionsSummary(conditions: ConditionalAccessPolicy['conditions']): string {
  const parts: string[] = [];
  if (conditions.trust_level?.length) parts.push(`Trust: ${conditions.trust_level.join(', ')}`);
  if (conditions.trust_score_below != null) parts.push(`Score < ${conditions.trust_score_below}`);
  if (conditions.os_types?.length) parts.push(`OS: ${conditions.os_types.join(', ')}`);
  if (conditions.user_roles?.length) parts.push(`Roles: ${conditions.user_roles.join(', ')}`);
  if (conditions.user_emails?.length) parts.push(`${conditions.user_emails.length} user(s)`);
  if (conditions.ip_ranges?.length) parts.push(`${conditions.ip_ranges.length} IP range(s)`);
  if (conditions.outside_known_locations) parts.push('Outside known locations');
  if (conditions.outside_business_hours) parts.push('Outside business hours');
  if (conditions.days_of_week?.length) parts.push(`Days: ${conditions.days_of_week.join(', ')}`);
  return parts.length > 0 ? parts.join(' + ') : 'All traffic';
}

export function ConditionalAccessPage() {
  const { data, loading, error, refresh } = useApi<{ data: ConditionalAccessPolicy[] }>('/protect/conditional-access/policies');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<PolicyForm>(EMPTY_POLICY);
  const [saving, setSaving] = useState(false);

  const policies = data?.data ?? [];

  const handleToggle = useCallback(async (id: string) => {
    await post(`/protect/conditional-access/policies/${id}/toggle`);
    refresh();
  }, [refresh]);

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Delete this policy?')) return;
    await del(`/protect/conditional-access/policies/${id}`);
    refresh();
  }, [refresh]);

  const handleMovePriority = useCallback(async (index: number, direction: 'up' | 'down') => {
    const newOrder = [...policies];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    const temp = newOrder[index]!;
    newOrder[index] = newOrder[swapIdx]!;
    newOrder[swapIdx] = temp;
    const order = newOrder.map((p, i) => ({ id: p.id, priority: i + 1 }));
    await put('/protect/conditional-access/policies/reorder', { order });
    refresh();
  }, [policies, refresh]);

  const handleEdit = (policy: ConditionalAccessPolicy) => {
    setEditingPolicy({
      id: policy.id,
      name: policy.name,
      description: policy.description,
      enabled: policy.enabled,
      conditions: policy.conditions,
      actions: policy.actions,
    });
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingPolicy(EMPTY_POLICY);
    setShowEditor(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPolicy.id) {
        await put(`/protect/conditional-access/policies/${editingPolicy.id}`, editingPolicy);
      } else {
        await post('/protect/conditional-access/policies', editingPolicy);
      }
      setShowEditor(false);
      refresh();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Conditional Access</h2>
          <p className="text-sm text-gray-500 mt-1">Zero Trust access policies based on device trust, user identity, and location</p>
        </div>
        <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium">
          <Plus className="h-4 w-4" /> Add Policy
        </button>
      </div>

      {/* Policy Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conditions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effect</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Enabled</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {policies.map((policy, idx) => {
              const badge = EFFECT_BADGES[policy.actions.effect] ?? { bg: 'bg-gray-100', text: 'text-gray-700', label: policy.actions.effect };
              return (
                <tr key={policy.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMovePriority(idx, 'up')}
                        disabled={idx === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleMovePriority(idx, 'down')}
                        disabled={idx === policies.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 text-sm">{policy.name}</div>
                    {policy.description && <div className="text-xs text-gray-500 mt-0.5">{policy.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{conditionsSummary(policy.conditions)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleToggle(policy.id)}>
                      {policy.enabled ? (
                        <ToggleRight className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(policy)} className="text-gray-400 hover:text-gray-600">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(policy.id)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {policies.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">No policies configured</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Policy Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingPolicy.id ? 'Edit Policy' : 'Create Policy'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingPolicy.name ?? ''}
                  onChange={(e) => setEditingPolicy({ ...editingPolicy, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., Block untrusted devices"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingPolicy.description ?? ''}
                  onChange={(e) => setEditingPolicy({ ...editingPolicy, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Conditions */}
              <fieldset className="border border-gray-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-gray-700 px-2">Conditions</legend>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Trust Level</label>
                    <div className="flex gap-2">
                      {(['trusted', 'conditional', 'untrusted'] as const).map(level => (
                        <label key={level} className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={editingPolicy.conditions?.trust_level?.includes(level) ?? false}
                            onChange={(e) => {
                              const current = editingPolicy.conditions?.trust_level ?? [];
                              const next = e.target.checked ? [...current, level] : current.filter(l => l !== level);
                              setEditingPolicy({
                                ...editingPolicy,
                                conditions: { ...editingPolicy.conditions, trust_level: next.length > 0 ? next : undefined },
                              });
                            }}
                            className="rounded border-gray-300 text-emerald-600"
                          />
                          {level}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Trust Score Below</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={editingPolicy.conditions?.trust_score_below ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
                        setEditingPolicy({
                          ...editingPolicy,
                          conditions: { ...editingPolicy.conditions, trust_score_below: val },
                        });
                      }}
                      className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      placeholder="0-100"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editingPolicy.conditions?.outside_known_locations ?? false}
                      onChange={(e) => setEditingPolicy({
                        ...editingPolicy,
                        conditions: { ...editingPolicy.conditions, outside_known_locations: e.target.checked || undefined },
                      })}
                      className="rounded border-gray-300 text-emerald-600"
                    />
                    Outside known locations
                  </label>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editingPolicy.conditions?.outside_business_hours ?? false}
                      onChange={(e) => setEditingPolicy({
                        ...editingPolicy,
                        conditions: { ...editingPolicy.conditions, outside_business_hours: e.target.checked || undefined },
                      })}
                      className="rounded border-gray-300 text-emerald-600"
                    />
                    Outside business hours
                  </label>
                </div>
              </fieldset>

              {/* Actions */}
              <fieldset className="border border-gray-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-gray-700 px-2">Actions</legend>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Effect</label>
                    <select
                      value={editingPolicy.actions?.effect ?? 'block'}
                      onChange={(e) => setEditingPolicy({
                        ...editingPolicy,
                        actions: { ...editingPolicy.actions, effect: e.target.value as 'allow' | 'block' | 'limit' | 'step_up_auth' },
                      })}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="allow">Allow</option>
                      <option value="block">Block</option>
                      <option value="limit">Limit</option>
                      <option value="step_up_auth">Step-Up Auth</option>
                    </select>
                  </div>

                  {(editingPolicy.actions?.effect === 'step_up_auth' || editingPolicy.actions?.effect === 'limit') && (
                    <>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editingPolicy.actions?.require_mfa ?? false}
                          onChange={(e) => setEditingPolicy({
                            ...editingPolicy,
                            actions: { ...editingPolicy.actions, require_mfa: e.target.checked },
                          })}
                          className="rounded border-gray-300 text-emerald-600"
                        />
                        Require MFA
                      </label>
                      {editingPolicy.actions?.require_mfa && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">MFA Frequency (hours)</label>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={editingPolicy.actions?.mfa_frequency_hours ?? 4}
                            onChange={(e) => setEditingPolicy({
                              ...editingPolicy,
                              actions: { ...editingPolicy.actions, mfa_frequency_hours: parseInt(e.target.value, 10) },
                            })}
                            className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editingPolicy.actions?.read_only ?? false}
                          onChange={(e) => setEditingPolicy({
                            ...editingPolicy,
                            actions: { ...editingPolicy.actions, read_only: e.target.checked },
                          })}
                          className="rounded border-gray-300 text-emerald-600"
                        />
                        Read-only access
                      </label>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom Message</label>
                    <input
                      type="text"
                      value={editingPolicy.actions?.message ?? ''}
                      onChange={(e) => setEditingPolicy({
                        ...editingPolicy,
                        actions: { ...editingPolicy.actions, message: e.target.value },
                      })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      placeholder="Message shown to affected users"
                    />
                  </div>
                </div>
              </fieldset>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowEditor(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editingPolicy.name}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingPolicy.id ? 'Update Policy' : 'Create Policy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
